import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createAdminSetting, getAdminSettings, updateAdminSetting, type AdminSetting } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";

/**
 * `/admin/parametres` — équivalent de apps/web/app/(admin)/admin/parametres/page.tsx :
 * écran d'édition des `setting` (`packages/db/prisma/schema.prisma`).
 *
 * `value` reste édité comme texte JSON brut (un `<textarea>`, pas un
 * formulaire par type) : les paramètres ont des formes très différentes
 * (texte, nombre, liste, objet) — un éditeur JSON générique couvre tous les
 * cas sans construire un widget par type de valeur, exactement comme
 * l'original. Le parsing/validation JSON (`parseValueField`) est repris à
 * l'identique de `lib/admin-settings-actions.ts`, exécuté ici côté client
 * avant l'appel mutation plutôt que dans une Server Action.
 */
function parseValueField(raw: string): { ok: true; value: unknown } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, message: "La valeur ne peut pas être vide." };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return {
      ok: false,
      message:
        'Valeur JSON invalide — un texte doit être entre guillemets (ex. "10:00-18:00"), une liste entre crochets (ex. ["a","b"]).',
    };
  }
}

export function AdminParametresPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => getAdminSettings(undefined),
  });

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; message: string } | null>(null);
  const invalidateSettings = () => queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });

  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof createAdminSetting>[1]) => createAdminSetting(undefined, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Enregistré." });
      invalidateSettings();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, input }: { key: string; input: Parameters<typeof updateAdminSetting>[2] }) =>
      updateAdminSetting(undefined, key, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Enregistré." });
      invalidateSettings();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const key = String(formData.get("key") ?? "").trim();
    const scope = String(formData.get("scope") ?? "").trim();
    const isPublic = formData.get("isPublic") === "on";
    const valueRaw = String(formData.get("value") ?? "");

    if (!key) {
      setFeedback({ type: "error", message: "La clé est obligatoire." });
      return;
    }
    const parsed = parseValueField(valueRaw);
    if (!parsed.ok) {
      setFeedback({ type: "error", message: parsed.message });
      return;
    }

    createMutation.mutate(
      { key, value: parsed.value, scope: scope || undefined, isPublic },
      { onSuccess: () => formEl.reset() },
    );
  }

  function handleUpdateSubmit(event: FormEvent<HTMLFormElement>, key: string) {
    event.preventDefault();
    setFeedback(null);
    const formData = new FormData(event.currentTarget);
    const scope = String(formData.get("scope") ?? "").trim();
    const isPublic = formData.get("isPublic") === "on";
    const valueRaw = String(formData.get("value") ?? "");

    const parsed = parseValueField(valueRaw);
    if (!parsed.ok) {
      setFeedback({ type: "error", message: parsed.message });
      return;
    }

    updateMutation.mutate({ key, input: { value: parsed.value, scope: scope || undefined, isPublic } });
  }

  const settings = settingsQuery.data ?? [];
  const byScope = new Map<string, AdminSetting[]>();
  for (const setting of settings) {
    const scope = setting.scope ?? "(sans catégorie)";
    const list = byScope.get(scope) ?? [];
    list.push(setting);
    byScope.set(scope, list);
  }
  const scopes = [...byScope.keys()].sort();
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Paramètres</h1>
      <p className="mt-1 text-sm text-muted">
        Informations affichées sur le site et modifiables ici sans redéploiement — bandeau
        d&apos;annonce, contact WhatsApp/téléphone, horaires, etc. {settings.length} paramètre(s) au total.
      </p>

      {settingsQuery.isLoading && <p className="mt-4 text-sm text-muted">Chargement…</p>}
      {settingsQuery.isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(settingsQuery.error)}
        </p>
      )}

      {feedback && (
        <p
          className={[
            "mt-4 rounded-sm border px-4 py-3 text-sm",
            feedback.type === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-green-300 bg-green-50 text-green-700",
          ].join(" ")}
        >
          {feedback.message}
        </p>
      )}

      <div className="mt-6 space-y-8">
        {scopes.map((scope) => (
          <section key={scope}>
            <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">{scope}</h2>
            <div className="mt-2 space-y-3">
              {(byScope.get(scope) ?? []).map((setting) => (
                <SettingRow key={setting.key} setting={setting} onSubmit={handleUpdateSubmit} disabled={isPending} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="mt-10 rounded-sm border border-line bg-white p-4">
        <h2 className="font-display text-sm font-semibold text-ink">Nouveau paramètre</h2>
        <p className="mt-1 text-xs text-muted">
          La clé n&apos;est plus modifiable une fois créée (c&apos;est elle que le code référence).
        </p>
        <form onSubmit={handleCreateSubmit} className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="new-key" className="text-xs font-medium text-muted">
                Clé (ex. content.mon_texte)
              </label>
              <input
                id="new-key"
                name="key"
                type="text"
                required
                pattern="[a-z0-9_]+(\.[a-z0-9_]+)*"
                title="Minuscules, segments séparés par des points"
                className="w-64 rounded-sm border border-line px-3 py-2 text-sm text-ink"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="new-scope" className="text-xs font-medium text-muted">
                Catégorie (scope)
              </label>
              <input
                id="new-scope"
                name="scope"
                type="text"
                placeholder="content, support…"
                className="w-40 rounded-sm border border-line px-3 py-2 text-sm text-ink"
              />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-ink">
              <input type="checkbox" name="isPublic" />
              Public (visible sur le site)
            </label>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="new-value" className="text-xs font-medium text-muted">
              Valeur (JSON — un texte entre guillemets, une liste entre crochets…)
            </label>
            <textarea
              id="new-value"
              name="value"
              required
              rows={3}
              placeholder='"Un texte" ou ["a", "b"] ou {"cle": "valeur"}'
              className="w-full rounded-sm border border-line px-3 py-2 font-mono text-sm text-ink"
            />
          </div>
          <button
            type="submit"
            disabled={isPending}
            className="w-fit rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Créer
          </button>
        </form>
      </section>
    </div>
  );
}

function SettingRow({
  setting,
  onSubmit,
  disabled,
}: {
  setting: AdminSetting;
  onSubmit: (event: FormEvent<HTMLFormElement>, key: string) => void;
  disabled: boolean;
}) {
  return (
    <details className="rounded-sm border border-line bg-white">
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3">
        <span className="font-mono text-sm text-ink">{setting.key}</span>
        <span
          className={[
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
            setting.isPublic ? "bg-gold/20 text-golddark" : "bg-line text-muted",
          ].join(" ")}
        >
          {setting.isPublic ? "Public" : "Interne"}
        </span>
      </summary>
      <form
        onSubmit={(event) => onSubmit(event, setting.key)}
        className="flex flex-col gap-3 border-t border-line px-4 py-3"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor={`scope-${setting.key}`} className="text-xs font-medium text-muted">
              Catégorie (scope)
            </label>
            <input
              id={`scope-${setting.key}`}
              name="scope"
              type="text"
              defaultValue={setting.scope ?? ""}
              className="w-40 rounded-sm border border-line px-3 py-2 text-sm text-ink"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-ink">
            <input type="checkbox" name="isPublic" defaultChecked={setting.isPublic} />
            Public (visible sur le site)
          </label>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`value-${setting.key}`} className="text-xs font-medium text-muted">
            Valeur (JSON)
          </label>
          <textarea
            id={`value-${setting.key}`}
            name="value"
            rows={setting.value && typeof setting.value === "object" ? 5 : 2}
            defaultValue={JSON.stringify(setting.value, null, 2)}
            className="w-full rounded-sm border border-line px-3 py-2 font-mono text-sm text-ink"
          />
        </div>
        <button
          type="submit"
          disabled={disabled}
          className="w-fit rounded-sm border border-navy px-4 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
        >
          Enregistrer
        </button>
      </form>
    </details>
  );
}
